import React, {
  useId,
  useRef,
  useState,
} from "react";

import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useNavigate } from "react-router-dom";

const focusStyle = {
  borderRadius: 2,

  "&:focus-visible, &.Mui-focusVisible": {
    outline: "3px solid #111",
    outlineOffset: "3px",
  },
};

export default function ShippingMethodSelector({
  value,
  onChange,
  error = false,
  errorId,
  labelledBy,
  describedBy,
}) {
  const [openDialog, setOpenDialog] =
    useState(false);

  const [menuWidth, setMenuWidth] =
    useState(0);

  const selectRef = useRef(null);
  const formControlRef = useRef(null);

  const navigate = useNavigate();
  const generatedId = useId();

  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const selectId =
    `shipping-method-select-${generatedId}`;

  const dialogTitleId =
    `shipping-dialog-title-${generatedId}`;

  const dialogDescriptionId =
    `shipping-dialog-description-${generatedId}`;

  const handleSelect = (event) => {
    const selected = event.target.value;

    if (selected === "משלוח") {
      setOpenDialog(true);

      // המשלוח יאושר רק לאחר לחיצה
      // על כפתור "אישור משלוח"
      onChange("");
      return;
    }

    onChange(selected);
  };

  const handleApproveDelivery = () => {
    onChange("משלוח");
    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);

    window.setTimeout(() => {
      selectRef.current?.focus();
    }, 0);
  };

  const handleOpenShippingPolicy = () => {
    setOpenDialog(false);
    navigate("/shipping-policy");
  };

  const ariaDescribedBy = [
    describedBy,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  const commonSelectSx = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    direction: "rtl",

    minHeight: {
      xs: 52,
      sm: 48,
    },

    fontSize: "1rem",

    "& .MuiSelect-select": {
      textAlign: "right",
      whiteSpace: "normal",
      display: "flex",
      alignItems: "center",

      py: {
        xs: 1.5,
        sm: 1.25,
      },
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: "#111",
      borderWidth: "2px",
    },

    ...focusStyle,
  };

  return (
    <Box
      sx={{
        mt: {
          xs: 2,
          sm: 3,
        },

        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        direction: "rtl",
      }}
    >
      <FormControl
        ref={formControlRef}
        fullWidth
        error={error}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {isMobile ? (
          /*
           * בטלפון משתמשים ב־Select טבעי.
           * כך הרשימה נשארת בתוך המסך
           * ואינה נפתחת בצד.
           */
         <Select
  inputRef={selectRef}
  id={selectId}
  value={value || ""}
  onChange={handleSelect}
  onOpen={() => {
    const currentWidth =
      formControlRef.current
        ?.getBoundingClientRect()
        .width;

    setMenuWidth(currentWidth || 0);
  }}
  displayEmpty
  renderValue={(selected) => {
    if (!selected) {
      return (
        <Box
          component="span"
          sx={{
            color: "text.secondary",
          }}
        >
          בחר שיטת משלוח
        </Box>
      );
    }

    return selected === "איסוף עצמי"
      ? "איסוף עצמי מהחנות"
      : "משלוח";
  }}
  inputProps={{
    "aria-label": "בחירת שיטת משלוח",
    "aria-labelledby":
      labelledBy || undefined,
    "aria-required": "true",
    "aria-invalid": error
      ? "true"
      : "false",
    "aria-describedby":
      ariaDescribedBy || undefined,
  }}
  MenuProps={{
    disableScrollLock: true,
    marginThreshold: 8,

    anchorOrigin: {
      vertical: "bottom",
      horizontal: "right",
    },

    transformOrigin: {
      vertical: "top",
      horizontal: "right",
    },

    PaperProps: {
      dir: "rtl",

      sx: {
        mt: 0.5,
        width: menuWidth || "auto",
        minWidth: menuWidth || "auto",
        maxWidth: {
          xs: "calc(100vw - 16px)",
          sm: menuWidth || "auto",
        },
        boxSizing: "border-box",
        overflowX: "hidden",
      },
    },
MenuListProps: {
  "aria-label": "אפשרויות שיטת משלוח",

  sx: {
    width: "100%",
    boxSizing: "border-box",
    py: 0.5,

    "& .MuiMenuItem-root": {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      minHeight: 48,
      boxSizing: "border-box",
      textAlign: "right",
      justifyContent: "flex-start",
      whiteSpace: "normal",
      color: "#212121",
    },

    /* מעבר עם המקלדת לאפשרות */
    "& .MuiMenuItem-root.Mui-focusVisible": {
      backgroundColor: "#fce4ec !important",
      color: "#880e4f",
      fontWeight: 700,
      outline: "3px solid #111",
      outlineOffset: "-3px",
    },

    /* האפשרות שנבחרה */
    "& .MuiMenuItem-root.Mui-selected": {
      backgroundColor: "#fce4ec !important",
      color: "#880e4f",
      fontWeight: 700,
    },

    /* האפשרות שנבחרה וגם נמצאת בפוקוס */
    "& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": {
      backgroundColor: "#f8bbd0 !important",
      color: "#880e4f",
      outline: "3px solid #111",
      outlineOffset: "-3px",
    },

    "& .MuiMenuItem-root:hover": {
      backgroundColor: "#fff0f5",
    },
  },
},
  }}
  sx={commonSelectSx}
>
  <MenuItem value="" disabled>
    בחר שיטת משלוח
  </MenuItem>

  <MenuItem value="איסוף עצמי">
    איסוף עצמי מהחנות
  </MenuItem>

  <MenuItem value="משלוח">
    משלוח
  </MenuItem>
</Select>
        ) : (
          /*
           * במחשב משתמשים בתפריט MUI.
           */
          <Select
            inputRef={selectRef}
            id={selectId}
            value={value || ""}
            onChange={handleSelect}
            onOpen={() => {
              const currentWidth =
                formControlRef.current
                  ?.getBoundingClientRect()
                  .width;

              setMenuWidth(
                currentWidth || 0
              );
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Box
                    component="span"
                    sx={{
                      color:
                        "text.secondary",
                    }}
                  >
                    בחר שיטת משלוח
                  </Box>
                );
              }

              return selected ===
                "איסוף עצמי"
                ? "איסוף עצמי מהחנות"
                : "משלוח";
            }}
            inputProps={{
              "aria-label":
                "בחירת שיטת משלוח",

              "aria-labelledby":
                labelledBy || undefined,

              "aria-required": "true",

              "aria-invalid": error
                ? "true"
                : "false",

              "aria-describedby":
                ariaDescribedBy || undefined,
            }}
            MenuProps={{
              disableScrollLock: true,

              anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
              },

              transformOrigin: {
                vertical: "top",
                horizontal: "right",
              },

              PaperProps: {
                dir: "rtl",

                sx: {
                  mt: 0.5,

                  width:
                    menuWidth || "auto",

                  minWidth:
                    menuWidth || "auto",

                  maxWidth:
                    menuWidth || "auto",

                  boxSizing:
                    "border-box",

                  overflowX: "hidden",
                },
              },

              MenuListProps: {
                variant: "menu",

                "aria-label":
                  "אפשרויות שיטת משלוח",

                sx: {
                  width: "100%",
                  boxSizing:
                    "border-box",
                  py: 0.5,

                  "& .MuiMenuItem-root":
                  {
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    minHeight: 48,
                    boxSizing:
                      "border-box",
                    textAlign: "right",
                    justifyContent:
                      "flex-start",
                    whiteSpace: "normal",
                  },

                },
              },
            }}
            sx={commonSelectSx}
          >
            <MenuItem value="איסוף עצמי">
              איסוף עצמי מהחנות
            </MenuItem>

            <MenuItem value="משלוח">
              משלוח
            </MenuItem>
          </Select>
        )}
      </FormControl>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        dir="rtl"
        maxWidth={false}
        hideBackdrop
        disableScrollLock
        disableRestoreFocus
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        sx={{
          position: "fixed !important",
          inset: "0 !important",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",

          "& .MuiDialog-container": {
            width: "100%",
            maxWidth: "100%",
            minHeight: "100dvh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            p: {
              xs: 2,
              sm: 3,
            },
          },
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100vw - 32px)",
              sm: 420,
            },

            maxWidth: {
              xs: "calc(100vw - 32px)",
              sm: 420,
            },

            minWidth: 0,
            height: "auto",
            maxHeight: "calc(100dvh - 32px)",

            m: "0 !important",
            p: 0,

            position: "relative",
            boxSizing: "border-box",

            overflowX: "hidden",
            overflowY: "auto",

            borderRadius: 3,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",

            boxShadow:
              "0 8px 30px rgba(0, 0, 0, 0.18)",
          },
        }}
      >
        <DialogTitle
          id={dialogTitleId}
          component="h2"
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            pt: {
              xs: 2.5,
              sm: 2.5,
            },

            pb: 1,

            fontSize: {
              xs: "1.3rem",
              sm: "1.5rem",
            },

            fontWeight: 700,
            textAlign: "right",
          }}
        >
          אישור שיטת משלוח
        </DialogTitle>

        <DialogContent
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "visible",

            px: {
              xs: 2,
              sm: 3,
            },

            py: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Box
            id={dialogDescriptionId}
            sx={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              textAlign: "right",
            }}
          >
            <Typography
              component="p"
              sx={{
                m: 0,

                fontSize: {
                  xs: "1rem",
                  sm: "1.125rem",
                },

                lineHeight: 1.7,
                overflowWrap: "anywhere",
              }}
            >
              כרגע המשלוחים הם לירושלים
              והסביבה בלבד.
            </Typography>

            <Typography
              component="p"
              sx={{
                mt: 1.5,
                mb: 0,

                fontSize: {
                  xs: "1rem",
                  sm: "1.125rem",
                },

                lineHeight: 1.7,
                overflowWrap: "anywhere",
              }}
            >
              התשלום עבור המשלוח ייגבה
              בנפרד.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",

            display: "flex",

            flexDirection: {
              xs: "column",
              sm: "row",
            },

            alignItems: "stretch",

            gap: {
              xs: 1,
              sm: 1,
            },

            px: {
              xs: 2,
              sm: 3,
            },

            pt: {
              xs: 1,
              sm: 1.5,
            },

            pb: {
              xs: 2,
              sm: 2.5,
            },

            "& > :not(style) ~ :not(style)":
            {
              ml: "0 !important",
              mr: "0 !important",
            },
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={
              handleOpenShippingPolicy
            }
            fullWidth
            sx={{
              minHeight: 48,

              color: "#AD1457",
              borderColor: "#AD1457",

              fontSize: "1rem",
              whiteSpace: "normal",
              textAlign: "center",

              "&:hover": {
                borderColor: "#880e4f",
                backgroundColor: "#fff5f8",
              },

              ...focusStyle,
            }}
          >
            למחירון משלוחים
          </Button>

          <Box
            sx={{
              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              width: "100%",
              maxWidth: "100%",
              minWidth: 0,

              gap: 1,
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={
                handleApproveDelivery
              }
              autoFocus
              fullWidth
              sx={{
                minHeight: 48,

                backgroundColor:
                  "#D81B60",

                fontSize: "1rem",
                whiteSpace: "normal",
                textAlign: "center",

                "&:hover": {
                  backgroundColor:
                    "#AD1457",
                },

                ...focusStyle,
              }}
            >
              אישור משלוח
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleCloseDialog}
              fullWidth
              sx={{
                minHeight: 48,
                color: "#AD1457",
                fontSize: "1rem",

                ...focusStyle,
              }}
            >
              ביטול
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
